import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, FileSpreadsheet, Download } from "lucide-react";

const REQUIRED_COLS = ["commercial_name", "email"];
const KNOWN_COLS = [
  "commercial_name", "legal_name", "cif", "dni_nif", "email", "phone",
  "address", "contact_person", "category", "status", "website",
];

function parseCsv(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (l) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const c = l[i];
      if (c === '"') { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out.map((v) => v.trim());
  };
  const headers = split(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map((l) => {
    const values = split(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = values[i] ?? ""; });
    return obj;
  });
  return { headers, rows };
}

const SAMPLE = `commercial_name,legal_name,cif,dni_nif,email,phone,address,category,status,website
Botiga Exemple,Botiga Exemple SL,B12345678,,exemple@correu.cat,977000001,C/ Barcelona 1 Salou,moda,active,https://exemple.cat
Café Prova,,B22345678,,cafe@correu.cat,977000002,Av. Jaume I 3 Salou,restauracio,active,`;

export default function CsvImport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.rows.length === 0) return toast.error("El CSV està buit");
    const missing = REQUIRED_COLS.filter((c) => !parsed.headers.includes(c));
    if (missing.length > 0) return toast.error(`Falten columnes obligatòries: ${missing.join(", ")}`);
    setPreview(parsed);
  };

  const doImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/associates/import", { rows: preview.rows });
      toast.success(`Import: ${data.created} creats · ${data.skipped} descartats`);
      if (data.errors?.length) {
        console.warn("Errors d'import:", data.errors);
      }
      setPreview(null); setOpen(false);
      onImported && onImported();
    } catch (e) { toast.error(e.response?.data?.detail || "Error important"); }
    finally { setImporting(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "plantilla_associats.csv"; a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="csv-import-btn"><Upload className="w-4 h-4 mr-2" /> Importar CSV</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Importar associats des de CSV</DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-secondary/60 p-4 text-sm">
              <div className="font-semibold mb-2 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Format esperat</div>
              <div className="text-xs text-muted-foreground mb-3">Columnes obligatòries: <code className="font-mono">commercial_name</code>, <code className="font-mono">email</code>. Opcionals: {KNOWN_COLS.filter(c => !REQUIRED_COLS.includes(c)).map(c => <code key={c} className="font-mono mr-1">{c}</code>)}.</div>
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={downloadSample} data-testid="csv-sample-btn"><Download className="w-3.5 h-3.5 mr-1" /> Descarregar plantilla</Button>
            </div>
            <div onClick={() => fileRef.current?.click()} className="rounded-2xl border-2 border-dashed border-border hover:border-accent p-10 text-center cursor-pointer transition-colors" data-testid="csv-drop-zone">
              <Upload className="w-8 h-8 mx-auto text-accent" />
              <div className="font-medium mt-2">Clica per seleccionar un CSV</div>
              <div className="text-xs text-muted-foreground mt-1">Es mostrarà una previsualització abans d'importar</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="csv-file-input" />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-sm" data-testid="csv-preview-summary">
              <strong>{preview.rows.length}</strong> files detectades · Columnes: {preview.headers.map(h => (
                <code key={h} className={`font-mono mr-1 px-1 rounded ${KNOWN_COLS.includes(h) ? "bg-emerald-200 dark:bg-emerald-900" : "bg-amber-200 dark:bg-amber-900"}`}>{h}</code>
              ))}
              <div className="text-xs text-muted-foreground mt-1">Les columnes en groc no es reconeixen i s'ignoraran.</div>
            </div>

            <div className="border rounded-xl overflow-hidden max-h-96 overflow-y-auto" data-testid="csv-preview-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    {preview.headers.map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 20).map((r, i) => (
                    <TableRow key={i}>
                      {preview.headers.map(h => <TableCell key={h} className="text-xs">{r[h] || <span className="text-muted-foreground italic">—</span>}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.rows.length > 20 && <div className="text-xs text-muted-foreground p-3 text-center">…i {preview.rows.length - 20} files més</div>}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setPreview(null)}>Tornar</Button>
              <Button onClick={doImport} disabled={importing} data-testid="csv-import-confirm-btn">
                {importing ? "Important..." : `Importar ${preview.rows.length} associats`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
